import { useState } from "react"
import { trackException, flushTelemetry } from "@rawa/telemetry"
import { Button } from "../../../components/Button"
import typography from "@rawa/ui/styles/typography.module.css"
import styles from "./DiagnosticsPage.module.css"

export function DiagnosticsPage() {
  const [renderError, setRenderError] = useState(false)
  const [status, setStatus] = useState<Record<string, string>>({})

  if (renderError) throw new Error("Test render error from DiagnosticsPage")

  function triggerUnhandledRejection() {
    Promise.reject(new Error("Test unhandled rejection from DiagnosticsPage"))
    flushTelemetry()
    setStatus((s) => ({
      ...s,
      rejection: "Fired — check App Insights in ~30 sec",
    }))
  }

  function triggerManualException() {
    trackException(new Error("Test manual exception from DiagnosticsPage"), {
      source: "DiagnosticsPage",
    })
    flushTelemetry()
    setStatus((s) => ({
      ...s,
      manual: "Tracked — check App Insights in ~30 sec",
    }))
  }

  return (
    <div className={styles.page}>
      <h1 className={typography.h3}>Telemetry Diagnostics</h1>
      <p className={typography.body}>
        Triggers for verifying Application Insights is correctly wired. Only
        available in non-production environments.
      </p>

      <div className={styles.grid}>
        <div className={styles.card}>
          <h2 className={typography.h5}>Render error</h2>
          <p className={typography.bodySmall}>
            Throws during the next render cycle, caught by the ErrorBoundary.
            Reload the page to recover.
          </p>
          <Button variant="secondary" onClick={() => setRenderError(true)}>
            Throw render error
          </Button>
        </div>

        <div className={styles.card}>
          <h2 className={typography.h5}>Unhandled rejection</h2>
          <p className={typography.bodySmall}>
            Fires a rejected Promise with no catch handler, exercising the{" "}
            <code>unhandledrejection</code> listener.
          </p>
          <Button variant="secondary" onClick={triggerUnhandledRejection}>
            Fire unhandled rejection
          </Button>
          {status.rejection && (
            <p className={styles.status}>{status.rejection}</p>
          )}
        </div>

        <div className={styles.card}>
          <h2 className={typography.h5}>Manual exception</h2>
          <p className={typography.bodySmall}>
            Calls <code>trackException</code> directly. Confirms the SDK is
            initialised and the connection string is set.
          </p>
          <Button variant="secondary" onClick={triggerManualException}>
            Track manual exception
          </Button>
          {status.manual && <p className={styles.status}>{status.manual}</p>}
        </div>
      </div>
    </div>
  )
}
